/**
 * MapView
 *
 * The "hero" component of Kingdom Map.
 * Renders a Mapbox GL dark-mode map with:
 *   - Heatmap layer (low zoom)
 *   - Individual "Impact Pin" markers (high zoom)
 *   - Anti-scraping "Fog of War" overlay
 *   - Temporal playback control at bottom
 *   - Region search bar top-left
 */
import { useEffect, useRef, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import { useImpactEvents } from '@/hooks/useImpactEvents';
import { useMapStore } from '@/stores/mapStore';
import { MAP_DEFAULTS, HEATMAP_COLOR_RAMP } from '@/lib/constants';
import { debounce } from '@/lib/utils';
import TemporalSlider from './TemporalSlider';
import RegionSearch from './RegionSearch';
import FogOfWarOverlay from './FogOfWarOverlay';
import MapSkeleton from './MapSkeleton';

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN;
// Suppress Mapbox telemetry in dev — events.mapbox.com causes
// ERR_CERT_COMMON_NAME_INVALID in DDEV. EVENTS_URL is a getter-only
// property in GL v3, so we override it via defineProperty.
try {
  Object.defineProperty(mapboxgl.config, 'EVENTS_URL', {
    get: () => '',
    configurable: true,
  });
} catch {
  // Silently ignore if the property isn't configurable in this build.
}

// Track rapid zoom changes for anti-scraping
const ZOOM_CHANGE_WINDOW_MS = 2000;
const ZOOM_CHANGE_THRESHOLD = 4; // delta zoom units within window = trigger fog

export default function MapView() {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const zoomHistoryRef = useRef([]); // track { zoom, time } for fog-of-war detection

  const { data: geoJson, isLoading, isError } = useImpactEvents();

  const setMapBounds = useMapStore((s) => s.setMapBounds);
  const setMapZoom = useMapStore((s) => s.setMapZoom);
  const setSelectedFeature = useMapStore((s) => s.setSelectedFeature);
  const triggerFogOfWar = useMapStore((s) => s.triggerFogOfWar);
  const fogOfWarActive = useMapStore((s) => s.fogOfWarActive);

  // ── Initialise Mapbox GL ─────────────────────────────────────────────────
  useEffect(() => {
    if (mapRef.current) return;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: MAP_DEFAULTS.style,
      center: MAP_DEFAULTS.center,
      zoom: MAP_DEFAULTS.zoom,
      minZoom: MAP_DEFAULTS.minZoom,
      maxZoom: MAP_DEFAULTS.maxZoom,
      attributionControl: false,
    });

    map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-right');
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');

    map.on('load', () => {
      // ── Source ─────────────────────────────────────────────────────────
      map.addSource('impact-events', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        cluster: true,
        clusterMaxZoom: MAP_DEFAULTS.PIN_ZOOM_THRESHOLD,
        clusterRadius: 50,
      });

      // ── Heatmap layer (low zoom) ───────────────────────────────────────
      map.addLayer({
        id: 'impact-heatmap',
        type: 'heatmap',
        source: 'impact-events',
        maxzoom: MAP_DEFAULTS.PIN_ZOOM_THRESHOLD + 1,
        paint: {
          'heatmap-weight': [
            'interpolate', ['linear'],
            ['coalesce', ['get', 'total_impact'], ['get', 'impact_count'], 1],
            1, 0.2,
            1000, 1,
          ],
          'heatmap-intensity': [
            'interpolate', ['linear'], ['zoom'],
            0, 1,
            MAP_DEFAULTS.PIN_ZOOM_THRESHOLD, 3,
          ],
          'heatmap-color': HEATMAP_COLOR_RAMP,
          'heatmap-radius': [
            'interpolate', ['linear'], ['zoom'],
            0, 20,
            MAP_DEFAULTS.PIN_ZOOM_THRESHOLD, 60,
          ],
          'heatmap-opacity': [
            'interpolate', ['linear'], ['zoom'],
            MAP_DEFAULTS.PIN_ZOOM_THRESHOLD - 1, 0.85,
            MAP_DEFAULTS.PIN_ZOOM_THRESHOLD + 1, 0,
          ],
        },
      });

      // ── Cluster circles (mid zoom) ─────────────────────────────────────
      map.addLayer({
        id: 'impact-clusters',
        type: 'circle',
        source: 'impact-events',
        filter: ['has', 'point_count'],
        minzoom: MAP_DEFAULTS.PIN_ZOOM_THRESHOLD - 1,
        paint: {
          'circle-color': [
            'step', ['get', 'point_count'],
            '#f97316',   10,
            '#ea580c',   50,
            '#dc2626',
          ],
          'circle-radius': [
            'step', ['get', 'point_count'],
            20,  10,
            30,  50,
            40,
          ],
          'circle-opacity': 0.85,
          'circle-stroke-width': 2,
          'circle-stroke-color': 'rgba(255,255,255,0.3)',
        },
      });

      // ── Cluster count labels ───────────────────────────────────────────
      map.addLayer({
        id: 'impact-cluster-count',
        type: 'symbol',
        source: 'impact-events',
        filter: ['has', 'point_count'],
        minzoom: MAP_DEFAULTS.PIN_ZOOM_THRESHOLD - 1,
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 13,
        },
        paint: { 'text-color': '#ffffff' },
      });

      // ── Individual pins (high zoom) ────────────────────────────────────
      map.addLayer({
        id: 'impact-pins',
        type: 'circle',
        source: 'impact-events',
        filter: ['!', ['has', 'point_count']],
        minzoom: MAP_DEFAULTS.PIN_ZOOM_THRESHOLD,
        paint: {
          'circle-color': '#f97316',
          'circle-radius': 7,
          'circle-stroke-width': 2,
          'circle-stroke-color': 'rgba(255,255,255,0.6)',
          'circle-opacity': 0.9,
        },
      });
    });

    // ── Viewport update handler ──────────────────────────────────────────
    const updateViewport = debounce(() => {
      const bounds = map.getBounds();
      const zoom = map.getZoom();
      setMapBounds([
        bounds.getWest(),
        bounds.getSouth(),
        bounds.getEast(),
        bounds.getNorth(),
      ]);
      setMapZoom(zoom);

      // Anti-scraping: track rapid zoom changes
      const now = Date.now();
      zoomHistoryRef.current.push({ zoom, time: now });
      // Only keep entries within the window
      zoomHistoryRef.current = zoomHistoryRef.current.filter(
        (e) => now - e.time < ZOOM_CHANGE_WINDOW_MS
      );
      if (zoomHistoryRef.current.length >= 2) {
        const oldest = zoomHistoryRef.current[0].zoom;
        const newest = zoomHistoryRef.current[zoomHistoryRef.current.length - 1].zoom;
        if (Math.abs(newest - oldest) >= ZOOM_CHANGE_THRESHOLD) {
          triggerFogOfWar(8);
        }
      }
    }, 200);

    map.on('moveend', updateViewport);
    map.on('zoomend', updateViewport);

    // ── Click handlers ───────────────────────────────────────────────────
    const handlePinClick = (e) => {
      const feature = e.features[0];
      setSelectedFeature(feature);
      // Fly to the clicked pin
      map.flyTo({ center: feature.geometry.coordinates, zoom: Math.max(map.getZoom(), 9), speed: 1.2 });
    };

    const handleClusterClick = (e) => {
      const feature = e.features[0];
      const clusterId = feature.properties.cluster_id;
      map.getSource('impact-events').getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return;
        map.easeTo({ center: feature.geometry.coordinates, zoom: zoom + 1 });
      });
    };

    map.on('click', 'impact-pins', handlePinClick);
    map.on('click', 'impact-clusters', handleClusterClick);

    // Pointer cursor on hover
    ['impact-pins', 'impact-clusters'].forEach((layer) => {
      map.on('mouseenter', layer, () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', layer, () => { map.getCanvas().style.cursor = ''; });
    });

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Sync GeoJSON data into the map source ─────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const data = geoJson ?? { type: 'FeatureCollection', features: [] };
    if (!map.isStyleLoaded()) {
      map.once('load', () => {
        map.getSource('impact-events')?.setData(data);
      });
      return;
    }
    map.getSource('impact-events')?.setData(data);
  }, [geoJson]);

  // ── Clear map source on logout ───────────────────────────────────────
  useEffect(() => {
    const handler = () => {
      const map = mapRef.current;
      if (!map || !map.isStyleLoaded()) return;
      map.getSource('impact-events')?.setData({ type: 'FeatureCollection', features: [] });
    };
    window.addEventListener('impact:map:clear', handler);
    return () => window.removeEventListener('impact:map:clear', handler);
  }, []);

  // ── Expose map instance to siblings via a custom event ───────────────
  // (Allows RegionSearch to call map.flyTo without prop drilling)
  useEffect(() => {
    const handler = (e) => {
      const map = mapRef.current;
      if (!map) return;
      const { center, zoom, bbox } = e.detail;
      if (bbox) {
        map.fitBounds(
          [[bbox[0], bbox[1]], [bbox[2], bbox[3]]],
          { padding: 80, duration: 1200 }
        );
      } else if (center) {
        map.flyTo({ center, zoom: zoom || 8, speed: 1.5 });
      }
    };
    window.addEventListener('km:map:flyTo', handler);
    return () => window.removeEventListener('km:map:flyTo', handler);
  }, []);

  return (
    <div className="relative h-full w-full">
      {/* Map canvas */}
      <div ref={mapContainerRef} className="absolute inset-0" />

      {/* Loading skeleton */}
      {isLoading && <MapSkeleton />}

      {/* Error state */}
      {isError && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="rounded-xl bg-red-50 border border-red-200 px-6 py-4 text-sm text-[#003cc5] backdrop-blur">
            Failed to load map data. Check your connection and try again.
          </div>
        </div>
      )}

      {/* Fog of War overlay */}
      {fogOfWarActive && <FogOfWarOverlay />}

      {/* Region search */}
      <div className="absolute left-4 top-4 z-10 w-72">
        <RegionSearch />
      </div>

      {/* Temporal slider */}
      <div className="absolute bottom-0 left-0 right-0 z-10">
        <TemporalSlider />
      </div>
    </div>
  );
}
